import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Divider,
  Grid2 as Grid,
  MenuList,
  Popover,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import TimezoneSelect from "../layout/TimezoneSelect";
import { UserAddOutlined, UserDeleteOutlined } from "@ant-design/icons";

import { logoutHandler } from "../../store/actions/sessionsActions";
import { useNavigate } from "react-router-dom";

export const AccountPopover = (props) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);

  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { anchorEl, onClose, open } = props;
  const fileInputRef = useRef(null);

  const removeAvatar = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/remove-avatar`,
        { currentSession: currentSession },

        {
          withCredentials: true,
        }
      );

      props?.setAvatarUrl("");
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (e) {}
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate the file size and type
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert("File is too large. Please select a file smaller than 5MB.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    const formData = new FormData();
    formData.append("uploaded_file", file);
    formData.append("currentSession", currentSession);

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/upload-avatar`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      props?.setAvatarUrl(resp?.data.data);
    } catch (e) {}
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const exitHandler = async () => {
    navigate("/login");
    await dispatch(logoutHandler(currentSession));
  };
  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: "left",
        vertical: "bottom",
      }}
      onClose={onClose}
      open={open}
      sx={{ width: "auto" }}
    >
      <Box
        sx={{
          py: 1.5,
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" align="center" sx={{ marginTop: "1rem" }}>
          {currentUser?.user && currentUser?.subuser
            ? currentUser?.subuser?.role
            : currentUser?.user?.role === "privateProvider"
              ? currentUser?.user?.privateProvider_license?.licenseType?.label
              : currentUser?.user?.role === "admin"
                ? "Super User"
                : currentUser?.user?.role === "tester"
                  ? "Tester"
                  : currentUser?.role === "notary" &&
                      currentUser?.is_notary_admin
                    ? "Notary Admin"
                    : currentUser?.role === "notary" &&
                        !currentUser?.is_notary_admin
                      ? "Notary User"
                      : "Administrator"}
        </Typography>
        <div
          style={{
            borderBottom: "3px solid #d3d3d3",
            marginTop: "3px",
          }}
        ></div>
        {currentUser?.role === "notary" ? (
          <>
            <Typography variant="overline" align="center">
              {currentUser?.full_name}
            </Typography>
          </>
        ) : (
          <>
            {currentUser?.user?.role === "privateProvider" ? (
              <Typography variant="overline" align="center">
                {currentUser?.user?.personal?.firstName +
                  " " +
                  currentUser?.user?.personal?.lastName}
              </Typography>
            ) : (
              <Typography variant="overline" align="center">
                {currentUser?.user &&
                  currentUser?.user?.personal?.[0]?.firstName +
                    " " +
                    currentUser?.user?.personal?.[0]?.lastName}
              </Typography>
            )}
          </>
        )}

        <Typography color="text.secondary" variant="body2">
          {currentUser?.user && currentUser?.subuser
            ? currentUser?.subuser?.email
            : currentUser?.role === "notary"
              ? currentUser?.email
              : currentUser?.user?.email}
        </Typography>
        <Divider />

        {currentUser?.subuser && (
          <>
            <div
              style={{
                borderBottom: "3px solid #d3d3d3",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            ></div>
            <Typography
              variant="overline"
              align="center"
              sx={{ marginTop: "1rem" }}
            >
              Your Administrator
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {typeof currentUser?.subuser?.admin === "string"
                ? currentUser.subuser.admin
                : ""}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {currentUser?.subuser && currentUser?.user?.email}
            </Typography>
          </>
        )}
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleAvatarChange}
        style={{ display: "none" }}
        accept="image/*"
      />
      <Typography align="center" variant="body2">
        Avatar
      </Typography>
      <Box
        sx={{
          borderTop: "1px solid #d3d3d3",

          pb: 1,
        }}
      >
        {props?.avatarUrl ? (
          <>
            <Grid container align="center" px={2} sx={{ width: "100%" }}>
              <Grid size={{ xs: 6 }}>
                <Tooltip title="Remove Avatar">
                  <IconButton
                    aria-label="add"
                    sx={{ color: "#f44336" }}
                    onClick={removeAvatar}
                  >
                    <UserDeleteOutlined />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Tooltip title="Change Avatar">
                  <IconButton
                    aria-label="add"
                    sx={{ color: "#1976d2" }}
                    onClick={triggerFileInput}
                  >
                    <UserAddOutlined />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ fontSize: "12px" }}
                >
                  Remove
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ fontSize: "12px" }}
                >
                  Change
                </Typography>
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid container align="center" px={2}>
              <Grid size={{ xs: 12 }}>
                <Tooltip title="Add Avatar">
                  <IconButton
                    aria-label="add"
                    sx={{ color: "#4caf50" }}
                    onClick={triggerFileInput}
                  >
                    <UserAddOutlined />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ fontSize: "12px" }}
                >
                  Add
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
      </Box>

      {tabletSize && (
        <Box sx={{ px: 1, pb: 1 }}>
          <TimezoneSelect
            options={props?.timezoneMappings}
            timezone={props?.timezone}
            setTimezone={props?.setTimezone}
          />
        </Box>
      )}

      <MenuList
        disablePadding
        dense
        sx={{
          "& > *": {
            borderRadius: 1,
          },
        }}
      >
        <LoadingButton
          loading={props?.signOutLoading}
          variant="contained"
          fullWidth
          size="small"
          onClick={exitHandler}
        >
          Sign out
        </LoadingButton>
      </MenuList>
    </Popover>
  );
};
